import get from 'lodash/get';
import sum from 'lodash/sum';
import find from 'lodash/find';
import round from 'lodash/round';

import groupBy from 'lodash/groupBy';
import flatMap from 'lodash/flatMap';
import reduce from 'lodash/reduce';
import moment from '@g4/moment-utc';

import { ALLEGIANT_FIRST_BUNDLE } from '../pageComponents/indexPage/constants';

const ROUND_TRIP_TYPE = 'ROUND_TRIP';

const GOVERNMENT_FEES = 'GOVERNMENT_FEES';
const AIRLINE_FEES = 'AIRLINE_FEES';

const feesGroups = {
  GOVERNMENT_FEES: [
    'XT',
    'SEC',
    'PFC',
    'XF',
    'ZP',
    'YC',
    'US1',
    'US',
    'UK',
    'AY',
    'XD',
    'XY',
    'XD2',
    'XO',
    'XD1',
    'XA',
    'S1',
  ],
  AIRLINE_FEES: ['CCV', 'BC'],
};

const getOrderItemsPriceTotal = (items) => {
  const amounts = items.map((item) => get(item, 'price.amount', 0));
  return sum(amounts);
};

const sumFeesGroupItems = (items) => sum(items.map((item) => item.amount));

const getAllFees = (itemTypePrices) => {
  let result = [];
  itemTypePrices.forEach((price) => {
    result.push(
      ...(price.taxes?.breakdown || []),
      ...(price.taxesIncludedInBundle?.breakdown || []),
      ...(price.fees?.breakdown || [])
    );
  });
  return result;
};

const mergeFees = (allFees) => {
  let result = [];
  let processedFeesCodes = [];

  allFees.forEach((fee) => {
    if (!processedFeesCodes.includes(fee.code)) {
      processedFeesCodes.push(fee.code);

      const allOfFeeType = allFees.filter(
        (unprocessedFee) => unprocessedFee.code === fee.code
      );
      const feeTotalAmount = sum(allOfFeeType.map((fee) => fee.value.amount));

      result.push({
        ...allOfFeeType[0],
        amount: feeTotalAmount,
      });
    }
  });

  return result;
};

const getIsRoundTripDiscountType = (flightsPrices) => {
  const discountTypes = flightsPrices.map(
    (flightPrices) => flightPrices.discountType
  );
  return discountTypes.every(
    (discountType) => discountType === ROUND_TRIP_TYPE
  );
};

export const getTripSummary = (order) => {
  const { items, travelers } = order;

  const orderItems = groupBy(items, '__typename');

  //Flights
  const flightOrderItems = (orderItems.FlightOrderItem ?? [])
    .filter(item => item.flight?.departingTime)
    .sort((a, b) => {
      const timeA = moment(a.flight.departingTime);
      const timeB = moment(b.flight.departingTime);
      return (timeA.isValid() && timeB.isValid()) ? timeA.valueOf() - timeB.valueOf() : 0;
    })
    .slice(0, 2);

  // Hotels
  const hotelItem = orderItems.HotelOrderItem?.[0];

  // Vehicles
  const vehicleItem = orderItems.VehicleOrderItem?.[0];

  // Bundles
  const bundleOrderItems = (orderItems.BundleOrderItem ?? []).filter(item => item.bundle.tier !== 4);

  // Seats
  const seatOrderItems = orderItems?.SeatOrderItem ?? [];

  // Shows
  const showItem = orderItems.ShowOrderItem ?? [];

  const flightsPrices = flightOrderItems.map((item) => item.flightPrice);
  const seatPrices = seatOrderItems.map((item) => item.seatPrice || {});

  // filter fees for a group - also keep items with same code together
  const getFeesByGroup = (group, mergedFees) =>
    flatMap(feesGroups[group], (code) =>
      mergedFees.filter((fee) => fee.code === code)
    );

  const allFlightFees = getAllFees(flightsPrices);
  const mergedFlightFees = mergeFees(allFlightFees);

  const allSeatFees = getAllFees(seatPrices);
  const mergedSeatFees = mergeFees(allSeatFees);
  const isBOGOApplied = flightOrderItems.some((item) => item.isBOGOApplied);

  const federalExciseTax = {
    air: find(mergedFlightFees, (fee) => fee.code === 'XT'),
    seats: find(mergedSeatFees, (fee) => fee.code === 'XT'),
  };

  // rename if FET on seats is present (TODO: check how translations for taxes work)
  if (federalExciseTax.seats != null) {
    federalExciseTax.seats.name += ' - Seats';
    if (federalExciseTax.air != null) {
      federalExciseTax.air.name += ' - Air';
    }
  }

  const flightOrderItemsTotalInclDiscount = reduce(
    flightOrderItems,
    (memo, item) => {
      let subtotal = get(item, 'flightPrice.subtotal', 0);
      let discount = get(item, 'flightPrice.discountValue.amount', 0);
      return memo + subtotal + (!isBOGOApplied ? discount : 0);
    },
    0
  );

  // total of hotel and vehicle price
  const totalOfShowHotelAndVehiclePrice = () => {
    let total = 0;
    if (hotelItem?.hotelPrice) {
      total += hotelItem.actualPrice.total;
    }
    if (vehicleItem?.vehiclePrice) {
      total += vehicleItem.vehiclePrice.total.amount;
    }
    if (showItem?.length) {
      const showItemsPriceTotal = showItem
        .map((item) => item.price.total.amount)
        .reduce((prev, next) => prev + next, 0);
      total += showItemsPriceTotal;
    }
    return total;
  };

  // seats price total
  // since XT is separated into government fees just get subtotal
  const seatsPriceTotal = reduce(
    seatOrderItems,
    (memo, item) => {
      let subtotal =
        get(item, 'seatPrice.subtotal', 0) || get(item, 'price.amount', 0);
      return memo + subtotal;
    },
    0
  );

  const flightHotelCarTotal =
    flightOrderItemsTotalInclDiscount + totalOfShowHotelAndVehiclePrice();

  const airlineFeesItems = getFeesByGroup(AIRLINE_FEES, mergedFlightFees);
  const airlineFeesTotal = sumFeesGroupItems(airlineFeesItems);
  const seatFeeTotal = sumFeesGroupItems(mergedSeatFees);

  const governmentFeesItems = getFeesByGroup(GOVERNMENT_FEES, [
    ...mergedFlightFees,
    ...mergedSeatFees,
  ]);
  const governmentFeesTotal = sumFeesGroupItems(governmentFeesItems);

  const sumTotalAmountFromFlightsPricesPath = (path) =>
    sum(flightsPrices.map((flightPrices) => get(flightPrices, path)));

  // Discount
  const flightsDiscountAmount = sumTotalAmountFromFlightsPricesPath(
    'discountValue.amount'
  );

  // Bogo discount
  const bogoFlightDiscountAmount = isBOGOApplied
    ? sumTotalAmountFromFlightsPricesPath('discountValue.amount')
    : 0;

  const isRoundTripDiscountType = getIsRoundTripDiscountType(flightsPrices);

  const itineraryAncillaryOrderItems =
    orderItems.ItineraryAncillaryOrderItem ?? [];

  const travelerAncillaryOrderItems =
    orderItems.TravelerAncillaryOrderItem ?? [];

  const ancillaryOrderItems = [
    ...travelerAncillaryOrderItems,
    ...itineraryAncillaryOrderItems,
  ];

  const ancillaryOrderItemsPriceTotal =
    seatsPriceTotal + getOrderItemsPriceTotal(ancillaryOrderItems);

  const ssrsArray = flatMap(travelers, (traveler) => traveler?.ssrs ?? []);

  const ssrsPriceTotal = getOrderItemsPriceTotal(ssrsArray);

  // Bags and Extras
  const bagsAndExtraItems = [
    ...itineraryAncillaryOrderItems,
    ...travelerAncillaryOrderItems,
    ...ssrsArray,
  ];

  // bags and extras total
  const bagsAndExtrasPriceTotal = reduce(
    bagsAndExtraItems,
    (memo, item) => {
      let subtotal = get(item, 'price.amount', 0);
      return memo + subtotal;
    },
    0
  );
  // bags and extras included in bundle
  const bundledBagsAndExtras = () => {
    if (bagsAndExtrasPriceTotal === 0) {
      return reduce(
        bagsAndExtraItems,
        (memo, item) => {
          if (item.isBundledAncillaryIncluded === true) {
            return true;
          }
          return memo;
        },
        false
      );
    }
    return false;
  };

  const isNewFareIncludedInBundle = bundleOrderItems.some(
    (item) => item.isBundledAncillaryIncluded === true && item.name === ALLEGIANT_FIRST_BUNDLE
  );

  // seats included in bundle
  const bundledSeats = () => {
    if (seatsPriceTotal === 0) {
      return reduce(
        seatOrderItems,
        (memo, item) => {
          // seatSizeId 7 is used to identify seats included in the new fare class bundle,
          // as no other unique identifier is provided in the response for this case
          if (item.isBundledAncillaryIncluded === true || Number(item.seatSizeId) === 7) {
            return true;
          }
          return memo;
        },
        false
      );
    }
    return false;
  };

  // adjust bundle price for taxes included in bundle
  const taxAdjustedBundleItem = () => {
    const taxIncludedInBundle = sum(
      seatPrices.map((price) => price.taxesIncludedInBundle?.total?.amount || 0)
    );

    if (taxIncludedInBundle === 0 || !bundleOrderItems?.length) {
      return bundleOrderItems;
    }

    // Handle array of bundle items (for departure and return flights)
    return bundleOrderItems.map((bundleItem) => {
      if (!bundleItem?.price) {
        return bundleItem;
      }

      const { amount, currency } = bundleItem.price;
      return {
        ...bundleItem,
        price: { amount: amount - taxIncludedInBundle, currency },
      };
    });
  };

  const ancillarySsrPriceTotal = round(
    ancillaryOrderItemsPriceTotal +
    ssrsPriceTotal +
    sum(
      taxAdjustedBundleItem()?.map((bundle) => bundle?.price?.amount || 0) ||
      []
    ),
    2
  );

  const bundleOrderItemsPriceTotal = sum(
    taxAdjustedBundleItem()?.map(bundle => bundle?.price?.amount || 0) || []
  );

  const isMinimumElementsForStadiumPackage =
    !orderItems.FlightOrderItem ||
    !orderItems.ShowOrderItem?.[0] ||
    !orderItems.HotelOrderItem?.[0];

  return {
    flightOrderItems,
    seatOrderItems,
    travelerAncillaryOrderItems,
    itineraryAncillaryOrderItems,
    hotelItem,
    vehicleItem,
    showItem,
    bundleOrderItems: taxAdjustedBundleItem(),
    bundledSeats: bundledSeats(),
    bundledBagsAndExtras: bundledBagsAndExtras(),
    bagsAndExtrasPriceTotal,
    ssrsPriceTotal,
    ancillaryOrderItemsPriceTotal,
    flightsDiscountAmount,
    governmentFeesItems,
    governmentFeesTotal,
    airlineFeesItems,
    airlineFeesTotal,
    flightHotelCarTotal,
    seatsPriceTotal,
    isRoundTripDiscountType,
    ancillarySsrPriceTotal,
    seatFeeTotal,
    isMinimumElementsForStadiumPackage,
    isBOGOApplied,
    bogoFlightDiscountAmount,
    bundleOrderItemsPriceTotal,
    isNewFareIncludedInBundle
  };
};
